import type { Task, TaskStatus } from "@/api.types.v2";
import { operationalDateKey } from "@/lib/operational-date";

function taskWindowBoundaries(task: Task): { start: Date; end: Date } | null {
  if (!task.scheduledWindow) return null;
  const start = new Date(task.scheduledWindow.startAt);
  const end = new Date(task.scheduledWindow.endAt);
  return Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())
    ? null
    : { start, end };
}

export type WindowState =
  | "completed"
  | "overdue"
  | "in_progress"
  | "starting_soon" // ≤15 min before start
  | "upcoming_soon" // 15–60 min before start
  | "upcoming";

const TERMINAL_STATUSES: TaskStatus[] = [
  "COMPLETED" as TaskStatus,
  "CANCELLED" as TaskStatus,
];

/**
 * Compute window state from current time. Pure function — easy to unit-test
 * without mocking clocks.
 */
export function windowState(task: Task, now: Date): WindowState | null {
  if (TERMINAL_STATUSES.includes(task.status)) return "completed";

  if (!task.scheduledWindow) {
    const scheduledDate = task.scheduledDate?.slice(0, 10);
    const currentOperationalDate = operationalDateKey(
      now,
      task.schedulingTimeZone
    );
    return scheduledDate &&
      currentOperationalDate &&
      scheduledDate < currentOperationalDate
      ? "overdue"
      : null;
  }

  const boundaries = taskWindowBoundaries(task);
  if (!boundaries) return null;
  const { start, end } = boundaries;

  if (now >= end) return "overdue";
  if (now >= start) return "in_progress";

  const minutesToStart = (start.getTime() - now.getTime()) / 60_000;
  if (minutesToStart <= 15) return "starting_soon";
  if (minutesToStart <= 60) return "upcoming_soon";
  return "upcoming";
}

/**
 * Urgency rim colour for map pins + chip background. Red is "do something
 * about this NOW," orange is "in your peripheral vision."
 *
 * Red:   window past end without completion, OR
 *        window in progress and task is unassigned, OR
 *        window starts in ≤15 min and task is unassigned
 * Orange: window starts in ≤60 min and task is unassigned (and not red)
 * Default: nothing — no visual urgency
 */
export function taskUrgency(task: Task, now: Date): "red" | "orange" | null {
  const state = windowState(task, now);
  if (state === null || state === "completed" || state === "upcoming") {
    return null;
  }

  const isUnassigned = !task.tripId && !task.operatorId;

  if (state === "overdue") return "red";
  if (state === "in_progress" && isUnassigned) return "red";
  if (state === "starting_soon" && isUnassigned) return "red";
  if (state === "upcoming_soon" && isUnassigned) return "orange";
  // in_progress + assigned, starting_soon + assigned, upcoming_soon + assigned:
  // someone's on it — no urgency
  return null;
}

/**
 * Filter the dispatch task list by the route-manager's date + window scope.
 *
 * Two axes combine here, plus one override:
 *
 *  1. DATE — the date pill's `selectedDate` ("YYYY-MM-DD"). Pickups use their
 *     booking window date; downstream work uses its canonical scheduledDate.
 *     Unscheduled work remains in the all-windows backlog.
 *  2. WINDOW — `windowFilter`:
 *       - null            → no window narrowing (date scope still applies)
 *       - "HH:mm__HH:mm"  → match scheduledWindow start+end. Keyed on
 *         (startTime, endTime) not slotId so the filter survives the
 *         multi-slot-per-window case (one window backed by N PickupSlot rows).
 *       - "overdue"       → only overdue tasks (the override below already
 *         keeps them; everything else is dropped).
 *       - legacy slotId   → direct slotId match (pre window-key migration).
 *     A specific window also excludes window-less backlog (it belongs to no
 *     window).
 *  3. OVERRIDE — overdue work is ALWAYS shown, bypassing both date and window.
 *     That includes a pickup whose window ended and downstream work whose
 *     scheduled day passed without reaching a terminal state.
 *
 * Lives next to the urgency helpers because filter and urgency share the
 * windowState classification.
 */
export function applyTaskScopeFilter(
  tasks: import("@/api.types.v2").Task[],
  selectedDate: string,
  windowFilter: string | null,
  now: Date
): import("@/api.types.v2").Task[] {
  const windowRange =
    windowFilter && windowFilter.indexOf("__") > 0
      ? windowFilter.split("__")
      : null;
  const [filterStart, filterEnd] = windowRange ?? [null, null];

  return tasks.filter((t) => {
    // Override: overdue tasks are never hidden, regardless of date/window.
    if (windowState(t, now) === "overdue") return true;
    // "overdue" filter wants only the overdue set (handled above).
    if (windowFilter === "overdue") return false;

    const w = t.scheduledWindow;
    const scheduledDate = w?.date ?? t.scheduledDate?.slice(0, 10) ?? null;

    if (scheduledDate && scheduledDate !== selectedDate) return false;

    // Unscheduled backlog stays visible under "all windows" and disappears
    // when the dispatcher chooses a specific pickup window.
    if (!w) return windowFilter === null;

    if (windowFilter === null) return true;
    if (windowRange)
      return w.startTime === filterStart && w.endTime === filterEnd;
    // Legacy slotId value from before the window-key migration.
    return w.slotId === windowFilter;
  });
}

export function taskScheduleLabel(task: Task, now: Date): string {
  if (task.scheduledWindow) {
    return `${task.scheduledWindow.date} · ${task.scheduledWindow.startTime}–${task.scheduledWindow.endTime}`;
  }
  const scheduledDate = task.scheduledDate?.slice(0, 10);
  if (!scheduledDate) return "Unscheduled · configure operational timing";
  return `${windowState(task, now) === "overdue" ? "Overdue" : "Scheduled"} · ${scheduledDate}`;
}

/**
 * Human-friendly relative-time label for the chip subtext.
 *   "in 35 min" / "starts in 2h" / "closing in 10 min" / "closed 30 min ago"
 * Returns null when the chip should show no relative time.
 */
export function windowRelativeTime(task: Task, now: Date): string | null {
  const boundaries = taskWindowBoundaries(task);
  if (!boundaries) return null;
  const { start, end } = boundaries;

  const minsToStart = Math.round((start.getTime() - now.getTime()) / 60_000);
  const minsToEnd = Math.round((end.getTime() - now.getTime()) / 60_000);

  if (minsToEnd < 0) return `closed ${Math.abs(minsToEnd)} min ago`;
  if (minsToStart < 0) return `closing in ${minsToEnd} min`;
  if (minsToStart < 60) return `in ${minsToStart} min`;
  const hours = Math.round(minsToStart / 60);
  return `in ${hours}h`;
}
